/**
 * AttachmentTile.jsx
 * Path: frontend/src/components/AttachmentTile.jsx
 * Description: Kafelek załącznika z miniaturą obrazu lub filmu oraz akcjami.
 * Related: components/AttachmentList.jsx, components/JobChat.jsx
 */

import { useEffect, useState } from "react";
import { Download, LoaderCircle, Paperclip, Play, Trash2 } from "lucide-react";
import { fetchJobAttachmentBlobUrl } from "../api/jobs";
import { formatAttachmentSize, isImageAttachment, isVideoAttachment } from "../utils/attachments";

export default function AttachmentTile({
  token,
  jobId,
  attachment,
  deleting = false,
  showDelete = false,
  onDownload,
  onDelete,
  onPreview,
}) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);

  const previewable = isImageAttachment(attachment) || isVideoAttachment(attachment);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";

    async function loadPreview() {
      if (!previewable || !token || !jobId || !attachment?.id) {
        setPreviewReady(false);
        setPreviewUrl("");
        return;
      }

      setPreviewLoading(true);
      try {
        objectUrl = await fetchJobAttachmentBlobUrl(token, jobId, attachment.id);
        if (!cancelled) {
          setPreviewUrl(objectUrl);
          setPreviewReady(true);
        }
      } catch (_error) {
        if (!cancelled) {
          setPreviewReady(false);
          setPreviewUrl("");
        }
        if (objectUrl) {
          window.URL.revokeObjectURL(objectUrl);
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    }

    loadPreview();

    return () => {
      cancelled = true;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [attachment?.id, jobId, previewable, token]);

  function handlePreview() {
    if (previewable && previewReady) {
      onPreview?.({ attachment, previewUrl });
    }
  }

  return (
    <article className={`attachment-tile ${previewable ? "attachment-tile-previewable" : ""}`}>
      <button
        type="button"
        className="attachment-tile-preview"
        onClick={handlePreview}
        disabled={!previewable || !previewReady}
        aria-label={`Podgląd ${attachment.originalName}`}
      >
        {previewLoading ? (
          <span className="attachment-preview-placeholder">
            <LoaderCircle size={18} className="spinning-icon" />
          </span>
        ) : null}
        {!previewLoading && isImageAttachment(attachment) && previewReady ? (
          <img src={previewUrl} alt={attachment.originalName} className="attachment-preview-image" />
        ) : null}
        {!previewLoading && isVideoAttachment(attachment) && previewReady ? (
          <span className="attachment-preview-video-shell">
            <video src={previewUrl} className="attachment-preview-video" muted playsInline preload="metadata" />
            <span className="attachment-preview-play">
              <Play size={18} />
            </span>
          </span>
        ) : null}
        {!previewLoading && (!previewable || !previewReady) ? (
          <span className="attachment-preview-placeholder">
            <Paperclip size={18} />
          </span>
        ) : null}
      </button>

      <div className="attachment-tile-body">
        <strong title={attachment.originalName}>{attachment.originalName}</strong>
        <div className="attachment-meta">
          <span>{attachment.mimeType || "plik"}</span>
          <span>{formatAttachmentSize(attachment.sizeBytes)}</span>
        </div>
      </div>

      <div className="attachment-actions attachment-actions-compact">
        <button type="button" className="ghost-button icon-button" onClick={() => onDownload?.(attachment)} aria-label="Pobierz">
          <Download size={16} />
        </button>
        {showDelete ? (
          <button
            type="button"
            className="ghost-button danger-button icon-button"
            onClick={() => onDelete?.(attachment)}
            disabled={deleting}
            aria-label="Usuń"
          >
            <Trash2 size={16} />
          </button>
        ) : null}
      </div>
    </article>
  );
}
